"use client";
import React, { useState, useEffect, useCallback } from "react";
import SortableCard from "./SortableCard";
import Card from "@/common/Card";
import { useSearchParams } from "next/navigation";
import { useMutation } from "@tanstack/react-query";
import { getSearchProducts } from "@/services/productService";
import { useInView } from "react-intersection-observer";
import { ProductType } from "@/types/productType";
import Spinner from "@/common/Spinner,";
import { searchResultType } from "@/types/Search";
import SearchFilterName from "../SearchFilter/SearchFilterName";

function SearchResult({
  minValue,
  maxValue,
  Categorykeys,
  brandKeys,
  setFilterCount,
  sortType,
  sortOrder,
  setSortOrder,
  setSortType,
  categoryNames,
  setCheckedBrandNames,
  setCheckedNames,
  brandNames,
  checkedItems,
  checkedNames,
  setCheckedBrandItems,
  checkedBrandItems,
  setCheckedItems,
  checkedBrandNames,
  MAX_VALUE,
  setMaxValue,
}: searchResultType) {
  const searchParams = useSearchParams();
  const specificValue = searchParams.get("q") || "";
  const finalSearchValue = decodeURIComponent(specificValue);
  const [debouncedMinValue, setDebouncedMinValue] = useState(minValue);
  const [debouncedMaxValue, setDebouncedMaxValue] = useState(maxValue);
  const { isPending, mutateAsync: getProductsItem } = useMutation({
    mutationFn: getSearchProducts,
    retry: 2,
    retryDelay: 1000,
  });

  const [productData, setProductData] = useState<ProductType[]>([]);

  const [itemsCount, setItemsCount] = useState<number>(0);
  const [page, setPage] = useState(0);
  const [loadMore, setLoadMore] = useState(true);

  const { ref, inView } = useInView();
  const isLoading = productData.length === 0 && loadMore;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedMinValue(minValue);
      setDebouncedMaxValue(maxValue);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [minValue, maxValue]);

  const getAllLastDiscountProduct = useCallback(async () => {
    const minPrice = debouncedMinValue;
    // const maxPrice = maxValue;
    const categoryIds = Categorykeys || [];
    const brandIds = brandKeys ?? [0];
    try {
      const requrstToserver = finalSearchValue
        ? {
            brandIds: brandIds ?? "",
            categoryIds: categoryIds ?? "",
            sortType: sortType,
            sortOrder: sortOrder,
            text: finalSearchValue,
            skip: page * 10,
            take: 10,
            minPrice,
            maxPrice: debouncedMaxValue + 1,
          }
        : {
            skip: page * 10,
            take: 10,
            minPrice,
            maxPrice: debouncedMaxValue + 1,
            categoryIds: categoryIds ?? "",
            brandIds: brandIds ?? "",
            sortType,
            sortOrder,
          };
      const response = await getProductsItem(requrstToserver);

      if (response?.success) {
        const newProducts = response?.products || [];
        setItemsCount((prevCount) => prevCount + newProducts.length);
        setProductData((prev) =>
          page === 0 ? newProducts : [...prev, ...newProducts]
        );
        setFilterCount(response?.filterCount);
        setMaxValue(response?.maxprice || MAX_VALUE);
        if (newProducts.length < 10) {
          setLoadMore(false);
        } else {
          setLoadMore(true);
        }
      }
    } catch (err) {
      console.error("Error in fetching products:", err);
      setLoadMore(false);
    }
  }, [
    finalSearchValue,
    getProductsItem,
    page,
    maxValue,
    debouncedMinValue,
    debouncedMaxValue,
    Categorykeys,
    brandKeys,
    sortOrder,
    sortType,
  ]);

  useEffect(() => {
    setProductData([]);
    setPage(0);
    setLoadMore(true);
    setItemsCount(0);
  }, [finalSearchValue, debouncedMinValue, Categorykeys]);

  useEffect(() => {
    if (inView && loadMore && !isPending) {
      setPage((prevPage) => prevPage + 1);
    }
  }, [inView, loadMore, isPending]);

  useEffect(() => {
    getAllLastDiscountProduct();
  }, [getAllLastDiscountProduct, debouncedMinValue, Categorykeys, maxValue]);

  return (
    <div className="mt-8 flex flex-col">
      <SortableCard
        setSortOrder={setSortOrder}
        setSortType={setSortType}
        sortType={sortType}
        sortOrder={sortOrder}
        itemsCount={itemsCount}
      />
      <SearchFilterName
        categoryNames={categoryNames}
        brandNames={brandNames}
        setCheckedBrandNames={setCheckedBrandNames}
        setCheckedNames={setCheckedNames}
        checkedItems={checkedItems}
        checkedNames={checkedNames}
        categoryKeys={Categorykeys}
        brandKeys={brandKeys}
        setCheckedItems={setCheckedItems}
        setCheckedBrandItems={setCheckedBrandItems}
        checkedBrandItmes={checkedBrandItems}
        checkedBrandNames={checkedBrandNames}
      />

      <div className="grid justify-center sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 min-[1400px]:grid-cols-5 min-[1400px]:gap-4  gap-10 2xl:gap-5 mt-5">
        {isLoading ? (
          ""
        ) : productData?.length > 0 ? (
          productData?.map((product) => (
            <Card
              key={product.variantId}
              {...product}
              hasBuy
              className="md:w-full"
            />
          ))
        ) : (
          <div className="lg:text-base text-lg text-red-500">
            موردی یافت نشد.
          </div>
        )}
      </div>
      {loadMore && (
        <div ref={ref} className="flex justify-center items-center mt-10">
          <Spinner />
        </div>
      )}
    </div>
  );
}

export default SearchResult;
